import { inject, Service } from '@angular/core';
import { HttpClient, HttpEvent, HttpEventType } from '@angular/common/http';
import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { API_CONFIGURATION } from '@lexplus-environments';
import { LexplusUploadStagingDto, uploadsControllerUploadDateiV1 } from '@lexplus-user-api';
import { uploadsControllerUploadDateiV1 as adminUploadsControllerUploadDateiV1 } from '@lexplus-admin-api';

/** The bytes are on their way; `progress` is a whole percentage between 0 and 100. */
export interface DateiUploadProgress {
  status: 'uploading';
  progress: number;
}

/** The bytes arrived and the server handed back a staging handle for the form to submit. */
export interface DateiUploadAbgeschlossen {
  status: 'complete';
  staging: LexplusUploadStagingDto;
}

export type DateiUploadEvent = DateiUploadProgress | DateiUploadAbgeschlossen;

/**
 * Uploads the bytes of one file and reports how far it got.
 *
 * These are the calls in the feature that are **not** made through the generated client:
 * `ng-openapi-gen` emits neither `reportProgress` nor `observe: 'events'`, so a generated call
 * can only report «done». The design shows a live progress bar.
 *
 * One endpoint per API, the same two-step flow on both: bytes first ({@link upload}), form afterwards,
 * so the duplicate warning can appear before submit.
 *
 * The paths still come from the generated functions, so a rename in the backend reaches here as
 * a compile error rather than a 404 at runtime. Credentials and the language header are added by
 * `ApiTokenInterceptor` like on any other request.
 */
@Service()
export class DokumentDateiUploadService {
  private readonly httpClient = inject(HttpClient);
  private readonly apiConfiguration = inject(API_CONFIGURATION);

  /**
   * Uploads one file to a staging area — the admin API's for a Kunde, the user API's otherwise. Unsubscribing aborts the request, which is
   * what the progress row's bin icon does before the upload has finished.
   *
   * @param {File} file - The file the user picked or dropped
   * @param {number | null} [kundeId] - The Kunde to stage it for through the admin API; `null` for the user API
   * @returns {Observable<DateiUploadEvent>} Progress while it uploads, then the staging handle
   */
  public upload(file: File, kundeId: number | null = null): Observable<DateiUploadEvent> {
    const formData = new FormData();
    formData.append('file', file, file.name);

    const url =
      kundeId === null
        ? `${this.apiConfiguration.baseUrlUser}${uploadsControllerUploadDateiV1.PATH}`
        : `${this.apiConfiguration.baseUrl}${adminUploadsControllerUploadDateiV1.PATH}`;
    const params: Record<string, number> = kundeId === null ? {} : { kundeId };

    return this.httpClient
      .post<LexplusUploadStagingDto>(url, formData, {
        params,
        reportProgress: true,
        observe: 'events',
      })
      .pipe(
        map((event): DateiUploadEvent | null => {
          const progress = this.toProgress(event);
          if (progress) {
            return progress;
          }
          if (event.type === HttpEventType.Response && event.body) {
            return { status: 'complete', staging: event.body };
          }
          return null;
        }),
        filter((event): event is DateiUploadEvent => event !== null),
      );
  }

  /**
   * Reads the upload progress off an event; every other event kind (sent, response header, the
   * response itself) is left to the caller.
   *
   * @param {HttpEvent<unknown>} event - An event of the upload request
   * @returns {DateiUploadProgress | null} The progress, or `null` for any other event
   */
  private toProgress(event: HttpEvent<unknown>): DateiUploadProgress | null {
    if (event.type !== HttpEventType.UploadProgress) {
      return null;
    }
    // `total` is absent for a stream of unknown length. The picked file always has a size, but
    // the type does not say so — 0% is the honest answer without it.
    return { status: 'uploading', progress: event.total ? Math.round((event.loaded / event.total) * 100) : 0 };
  }
}
